/**
 * Which league, if any, owns the address the browser is currently on.
 *
 * A league may bring its own domain. On it the platform disappears: the address
 * lands on that league rather than on the list of leagues, the crumb back to
 * "All leagues" is gone, and no other league can be reached. One deploy and one
 * database still serve everybody — what changes is only which front door was
 * used to arrive.
 *
 * Kept pure and away from the app so it can be checked without a browser, and
 * so the rule for "same host" is written once rather than guessed at each call.
 */

/**
 * A hostname reduced to what should be compared.
 *
 * Ports go because the same site is the same site on any port, `www.` goes
 * because nobody agrees on whether to type it, case goes because DNS does not
 * care, and the root dot goes because a fully-qualified name ends in one that
 * browsers occasionally keep.
 */
export function normaliseHost(host: string): string {
  return host.trim().toLowerCase().split(':')[0].replace(/\.$/, '').replace(/^www\./, '');
}

export interface HostedLeague {
  /** The league's own domain, or null when it has none. */
  hostname: string | null;
}

/**
 * The league that owns `host`, or null when the platform itself does.
 *
 * Null is the answer for 8ballpadel.com, for a deploy preview and for
 * localhost, none of which belong to a league — and the platform behaves
 * exactly as it always has when it gets one.
 */
export function homeLeague<T extends HostedLeague>(host: string, leagues: T[]): T | null {
  const wanted = normaliseHost(host);

  /*
   * The `Boolean(l.hostname)` guard is the load-bearing one, not decoration.
   * Without it a league whose hostname is unset normalises to '' and compares
   * equal to a request that arrived without a host — handing every such
   * request to whichever league happened to be first in the list.
   */
  return leagues.find((l) => Boolean(l.hostname) && normaliseHost(l.hostname as string) === wanted) ?? null;
}

/**
 * The address the visitor is actually on, tidied for printing.
 *
 * Used wherever the site names itself. Written as the current host rather than
 * as a constant so it reads "8ballpadel.com" on the platform and the league's
 * own domain on theirs — a sponsor invitation that quotes the wrong address is
 * an invitation to somebody else's site.
 */
export function displayHost(host: string): string {
  return normaliseHost(host);
}
